const API_BASE = ''

/**
 * 이미지 URL을 상대경로로 변환
 *
 * S3 업로드 URL이 WEB_URL(예: https://fiket.solboxdev.com) 기반 절대경로로 저장되는데,
 * Admin은 다른 도메인(admin.fiket.solboxdev.com)이라 CORP 차단됨.
 * `/api/uploads/...` 상대경로로 변환하면 Admin 자체 CloudFront 프록시를 타서 해결.
 */
/**
 * 이미지 URL을 상대경로로 변환 (단일 문자열)
 */
export function toLocalImageUrl(url: string): string {
  try {
    const parsed = new URL(url)
    if (parsed.pathname.startsWith('/api/uploads/')) {
      return parsed.pathname
    }
  } catch {
    // 이미 상대경로이거나 파싱 불가
  }
  return url
}

/**
 * API 응답 내 모든 /api/uploads/ URL을 상대경로로 재작성
 *
 * Admin이 Web과 다른 도메인이라 CloudFront CORP(same-origin)에 차단됨.
 * 상대경로로 바꾸면 Admin 자체 프록시를 타서 해결.
 */
function rewriteImageUrls<T>(data: T): T {
  if (data == null || typeof data !== 'object') return data
  if (Array.isArray(data)) return data.map(rewriteImageUrls) as T
  const obj = data as Record<string, unknown>
  const result: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'string' && value.includes('/api/uploads/')) {
      result[key] = toLocalImageUrl(value)
    } else if (Array.isArray(value)) {
      result[key] = value.map(v =>
        typeof v === 'string' && v.includes('/api/uploads/') ? toLocalImageUrl(v) : rewriteImageUrls(v)
      )
    } else if (typeof value === 'object' && value !== null) {
      result[key] = rewriteImageUrls(value)
    } else {
      result[key] = value
    }
  }
  return result as T
}

/**
 * 현재 admin 도메인에 대응하는 web(청첩장) 도메인 origin을 반환
 *
 * 청첩장 뷰어(`/letter`, `/inv`)·공유 에셋(`/assets/...`)은 web 도메인에서만 서빙된다.
 * admin 도메인 상대경로로 열면 admin.fiket.ai 로 연결돼 깨지므로 web origin으로 치환한다.
 * dev/prod 변환 규칙이 달라(`admin.` strip vs `wedding.` 치환) 명시적으로 매핑한다.
 */
export function webOrigin(): string {
  const { origin, hostname } = window.location
  // prod
  if (hostname === 'admin.fiket.ai') return 'https://wedding.fiket.ai'
  // dev (solboxdev)
  if (hostname === 'admin.fiket.solboxdev.com') return 'https://fiket.solboxdev.com'
  // 로컬 dev: admin :5175 → web :5173
  if (hostname === 'localhost' || hostname === '127.0.0.1') return origin.replace(':5175', ':5173')
  // 알 수 없는 도메인: 선두 `admin.` 제거 폴백
  return origin.replace('//admin.', '//')
}

/** web 도메인 기준 절대 URL 생성 */
export function webUrl(path: string): string {
  return `${webOrigin()}${path.startsWith('/') ? path : `/${path}`}`
}

/** 동시 401 응답 시 refresh 요청을 한 번만 보내기 위한 공유 Promise */
let refreshPromise: Promise<boolean> | null = null

interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: { code: string; message: string }
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers)
  if (!headers.has('Content-Type') && init?.body && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json')
  }

  let res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
    credentials: 'include',
  })

  // 401이면 쿠키 기반 토큰 갱신 시도 (동시 요청 시 1회만 호출)
  if (res.status === 401) {
    if (!refreshPromise) {
      refreshPromise = fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      })
        .then((r) => r.ok)
        .finally(() => { refreshPromise = null })
    }
    const refreshed = await refreshPromise

    if (refreshed) {
      res = await fetch(`${API_BASE}${path}`, {
        ...init,
        headers,
        credentials: 'include',
      })
    }
  }

  const json: ApiResponse<T> = await res.json()
  if (json.success === false) {
    const msg = json.error?.message ?? 'Unknown error'
    throw new Error(msg)
  }
  return rewriteImageUrls(json.data as T)
}
